<template>
  <div class="main-container">
    <!-- Header Section -->
    <v-row class="pa-2 header-row">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <!-- Back Button -->
        <v-btn icon class="back-button my-4 mx-4" @click="goBack">
          <v-icon class="white--text mx-4">mdi-arrow-left</v-icon>
        </v-btn>

        <!-- Title -->
        <div class="page-title">My Active Jobs</div>
        
        <!-- No Add Button - Activity view is read-only -->
      </v-col>
    </v-row>

    <!-- Loader -->
    <v-row v-if="loading" justify="center" align="center" class="loader-row">
      <v-progress-circular indeterminate color="#ff3b47"></v-progress-circular>
    </v-row>

    <!-- Jobs Grid -->
    <div v-if="!loading" class="jobs-container">
      <!-- Info Alert -->
      <v-alert
        type="info"
        color="#e3f2fd"
        text-color="#1976d2"
        border="left"
        class="mb-4"
        style="border-left-color: #2196f3 !important;"
      >
        <div style="color: #1565c0;">
          <strong>Your Active Jobs:</strong> This shows only jobs assigned to you that are currently being worked on (not completed).
        </div>
      </v-alert>

      <!-- Search -->
      <v-row justify="center" class="search-filter-row">
        <v-col cols="12" md="6">
          <v-text-field
            class="pa-3 white-search-field"
            filled dense rounded
            v-model="search"
            append-icon="mdi-magnify"
            label="Search Your Jobs"
            single-line hide-details
          />
        </v-col>
      </v-row>

      <!-- No Jobs Message -->
      <div v-if="filteredJobs.length === 0 && !loading" class="no-jobs-message">
        <v-icon size="64" color="grey">mdi-briefcase-outline</v-icon>
        <div class="text-h6 mt-4 grey--text">No active jobs assigned to you</div>
        <div class="text-body-2 grey--text mt-2">
          Jobs will appear here when they are assigned to you and not yet completed.
        </div>
      </div>

      <!-- Job Cards -->
      <v-row v-else>
        <v-col
          v-for="job in paginatedJobs"
          :key="job.id"
          cols="12" sm="6" md="4" lg="3"
        >
          <v-card
            class="modern-card"
            elevation="6" hover
            @click="handleCardClick(job)"
          >
            <!-- Chips & Info Button -->
            <v-row class="d-flex justify-end pr-3 pt-3 align-center">
              <v-chip :color="getChipColor(job.status)" dark label class="status-chip ml-2">
                {{ job.status || 'Unknown' }}
              </v-chip>
              <v-chip color="grey" dark label class="job-number-chip ml-2">
                Job #: {{ job.jobNumber || 'N/A' }}
              </v-chip>
              <v-btn icon @click.stop="openExtraInfo(job)" title="View Details" class="ml-2">
                <v-icon color="#ff3b47">mdi-information</v-icon>
              </v-btn>
            </v-row>

            <!-- Card Body -->
            <v-card-text class="pa-4">
              <v-row><v-col class="py-2"><span class="text-caption">Customer:</span>
                <span class="font-weight-medium">{{ job.customerName }}</span></v-col></v-row>
              <v-row><v-col class="py-2"><span class="text-caption">Seal Type:</span>
                <span class="font-weight-medium">{{ job.jobName }}</span></v-col></v-row>
              <v-row><v-col class="py-2"><span class="text-caption">Created:</span>
                <span class="font-weight-medium">{{ formatDate(job.currentDate) }}</span></v-col></v-row>
              <v-row><v-col class="py-2"><span class="text-caption">Total:</span>
                <span class="font-weight-medium">{{ formatCurrency(job.grandTotal) }}</span></v-col></v-row>
            </v-card-text>

            <!-- Open Job Button -->
            <v-card-actions class="d-flex justify-between align-center">
              <v-btn
                rounded dark color="#ff3b47"
                @click.stop="handleCardClick(job)"
                class="open-job-button flex-grow-1 mr-2"
              >
                Open Job
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Load More -->
      <v-row justify="center" v-if="canLoadMore">
        <v-btn color="#ff3b47" @click="loadMore" class="load-more-button">
          Load More
        </v-btn>
      </v-row>
    </div>

    <!-- Extra-Info Dialog -->
    <v-dialog v-model="extraInfoDialog.visible" max-width="600" class="extra-info-dialog">
      <v-card>
        <v-card-title class="headline dialog-title">
          Job Details
          <v-spacer/>
          <v-btn icon @click="extraInfoDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Job #:</strong> {{ extraInfoJob.jobNumber }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Customer:</strong> {{ extraInfoJob.customerName }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Type:</strong> {{ extraInfoJob.jobName }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Size:</strong> {{ extraInfoJob.ShaftSize }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Status:</strong> {{ extraInfoJob.status }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Cust Ref:</strong> {{ extraInfoJob.customerReference }}
            </v-list-item-title></v-list-item-content></v-list-item>

            <v-list-item><v-list-item-content><v-list-item-title>
              <strong>Summary:</strong> {{ extraInfoJob.JobSummary }}
            </v-list-item-title></v-list-item-content></v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, doc, getDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

export default {
  name: "ActivityJobs",
  data() {
    return {
      jobs: [],
      search: "",
      userRole: "",
      userEmail: "",
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      visibleCount: 12,
      loadIncrement: 12,
      loading: false,
      extraInfoDialog: { visible: false },
      extraInfoJob: {}
    };
  },

  computed: {
    filteredJobs() {
      let f = Array.isArray(this.jobs) ? [...this.jobs] : [];
      if (this.search) {
        const q = this.search.toLowerCase();
        f = f.filter(j =>
          j.jobName?.toLowerCase().includes(q) ||
          String(j.jobNumber).toLowerCase().includes(q) ||
          j.customerName?.toLowerCase().includes(q)
        );
      }
      return f.sort((a, b) => (+b.jobNumber || 0) - (+a.jobNumber || 0));
    },
    paginatedJobs() { return this.filteredJobs.slice(0, this.visibleCount); },
    canLoadMore() { return this.visibleCount < this.filteredJobs.length; }
  },

  watch: {
    search() { this.resetPagination(); }
  },

  async created() {
    this.loading = true;
    await this.fetchUserInfo();
    await this.fetchUserJobs();
    this.loading = false;
  },

  methods: {
    /** USER INFO **/
    async fetchUserInfo() {
      const current = auth.currentUser;
      if (current) return this.loadUserDocument(current.uid);

      await new Promise(resolve => {
        onAuthStateChanged(auth, user => {
          if (user) this.loadUserDocument(user.uid).finally(resolve);
          else resolve();
        });
      });
    },

    async loadUserDocument(uid) {
      try {
        const uSnap = await getDoc(doc(db, "users", uid));
        if (uSnap.exists()) {
          const d = uSnap.data();
          this.userRole = d.role || "";
          this.userEmail = d.email?.toLowerCase() || "";
        }
      } catch (e) {
        console.error("User doc error:", e);
        this.showSnackbar("Failed to load user info", "error");
      }
    },

    /** FETCH USER-SPECIFIC JOBS **/
   async fetchUserJobs() {
      try {
        const snap = await getDocs(collection(db, "jobs"));
        const email = this.userEmail;
        
        // Define active statuses (inspection to ready)
        const activeStatuses = ['inspection', 'costing complete', 'quoted', 'inprogress', 'ready'];
        
        // Filter jobs assigned to current user with active statuses only
        this.jobs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(job => {
            const data = job || {};
            const assigned =
              (data.engineer?.email?.toLowerCase()   === email) ||
              (data.technician?.email?.toLowerCase() === email) ||
              (data.salesperson?.email?.toLowerCase()=== email);
            
            const hasActiveStatus = activeStatuses.includes(data.status?.toLowerCase());
            
            return assigned && hasActiveStatus;
          });

        console.log(`Found ${this.jobs.length} active jobs for user`);
      } catch (e) {
        console.error("Fetch jobs error:", e);
        this.showSnackbar("Failed to fetch jobs", "error");
      }
    },

    /** NAVIGATION **/
    goBack() { this.$router.go(-1); },

    /** CARD ACTIONS **/
    handleCardClick(job) {
      localStorage.setItem("selectedJob", job.id);
      const routes = {
        Salesperson: "/SalesVB",
        Technician: "/TechVB",
        Engineer: "/ViewJob"
      };
      this.$router.push(routes[this.userRole] || "/ViewJob");
    },

    /** PAGINATION **/
    loadMore() { this.visibleCount += this.loadIncrement; },
    resetPagination() { this.visibleCount = this.loadIncrement; },

    /** UI HELPERS **/
    getChipColor(status) {
      const map = {
        "job order": "blue",
        inspection: "orange",
        "costing complete": "teal",
        quoted: "purple",
        inprogress: "light-blue",
        ready: "green",
        completed: "dark-green",
        delayed: "brown",
        canceled: "red"
      };
      return map[status?.toLowerCase()] || "grey";
    },

    formatDate(s) {
      const d = new Date(s);
      return isNaN(d) ? s : d.toLocaleDateString(undefined,{ year:"numeric", month:"short", day:"numeric"});
    },

    formatCurrency(a) {
      return new Intl.NumberFormat("en-ZA",{style:"currency",currency:"ZAR"}).format(+a||0);
    },

    showSnackbar(msg, color="success") { 
      this.snackbarMessage=msg; 
      this.snackbarColor=color; 
      this.snackbar=true; 
    },

    /** EXTRA-INFO **/
    openExtraInfo(job) { 
      this.extraInfoJob = job; 
      this.extraInfoDialog.visible = true; 
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

.main-container { background:#121212; min-height:100vh; }

/* Header */
.header-row { background:#1e1e1e; border-bottom:1px solid #333; }
.page-title { color: white; font-size: 1.25rem; font-weight: 600; }

/* Jobs container */
.jobs-container { background:#fff; border-radius:20px 20px 0 0; padding:24px; box-shadow:0 4px 15px rgba(0,0,0,.1); }
.search-filter-row { margin-bottom:24px; }
.white-search-field .v-input__control { background:#fff; border-radius:8px; }

/* No jobs message */
.no-jobs-message {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

/* Cards */
.modern-card { 
  background:#fff; 
  border-radius:16px; 
  transition:.3s; 
  cursor:pointer; 
  margin:8px; 
  height:260px; 
  display:flex; 
  flex-direction:column; 
  justify-content:space-between; 
}
.modern-card:hover { transform:translateY(-5px); box-shadow:0 8px 25px rgba(0,0,0,.2); }

/* Chips */
.status-chip,.job-number-chip { font-size:.75rem; text-transform:capitalize; }

/* Buttons */
.open-job-button { font-weight:600; transition:.3s; }
.open-job-button:hover { background:#e63946; transform:scale(1.02); }

.back-button { color:#fff; border-radius:50%; transition:.3s; }
.back-button:hover { transform:scale(1.1); }

.load-more-button { font-weight:600; transition:.3s; margin-top: 20px; }
.load-more-button:hover { background:#e63946; transform:scale(1.02); }

.loader-row { margin-top:50px; }

/* Dialog */
.extra-info-dialog .v-card { border-radius:16px; }
</style>